import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { TranslocoModule } from '@jsverse/transloco';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { NameInitialsComponent } from '../../../shared/component/name-initials/name-initials.component';
import { QrCodeInfoStore } from './qr-code-info.store';
import { LoadingStatus } from '../../../shared/enums/loading';
import { LoadingSpinnerComponent } from '../../../shared/component/loading-spinner/loading-spinner.component';

@Component({
  selector: 'anihis-qr-code-info',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    MatCardModule,
    MatIconModule,
    NameInitialsComponent,
    LoadingSpinnerComponent,
  ],
  templateUrl: './qr-code-info.component.html',
  styleUrl: './qr-code-info.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [QrCodeInfoStore],
})
export class QrCodeInfoComponent {
  store = inject(QrCodeInfoStore);
  readonly status = LoadingStatus;

  private normalizeFacebookUrl(value: string): string {
    const trimmed = value.trim();
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed;
    }

    if (/^www\./i.test(trimmed) || /facebook\.com\//i.test(trimmed)) {
      return `https://${trimmed}`;
    }

    return `https://www.facebook.com/${trimmed.replace(/^@/, '')}`;
  }

  redirectToWebsite(): void {
    let website = this.store.data()?.contact?.website;

    if (!website) return;

    if (!/^https?:\/\//i.test(website)) {
      website = `https://${website}`;
    }

    window.open(website, '_blank');
  }

  redirectToMap() {
    if (!this.store.data()?.contact?.street) return;
    const encodedAddress = encodeURIComponent(
      this.store.data()?.contact?.street as string,
    );
    const googleMapsUrl = `https://www.google.com/maps?q=${encodedAddress}`;
    window.open(googleMapsUrl, '_blank');
  }

  redirectToPhoneNumber(): void {
    window.open(`tel:${this.store.data()?.contact?.phoneNumber}`, '_self');
  }

  redirectToMobileNumber(): void {
    window.open(`tel:${this.store.data()?.contact?.mobileNumber}`, '_self');
  }

  redirectToEmail(): void {
    const mailtoUrl = `mailto:${this.store.data()?.email}`;
    window.open(mailtoUrl, '_self');
  }

  redirectToInstagram(): void {
    const url = `https://www.instagram.com/${this.store.data()?.contact?.instagram}`;
    window.open(url, '_blank');
  }

  redirectToFacebook(): void {
    const facebook = this.store.data()?.contact?.facebook;
    if (!facebook) return;

    const url = this.normalizeFacebookUrl(facebook);
    window.open(url, '_blank');
  }

  redirectToLinkedIn(): void {
    const url = `https://www.linkedin.com/in/${this.store.data()?.contact?.linkedIn}`;
    window.open(url, '_blank');
  }
}
